import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from './types';

interface FieldCardProps {
  field: FormField;
  index: number;
  totalFields: number;
  onDelete: (fieldId: string) => void;
  onMoveUp: (fieldId: string) => void;
  onMoveDown: (fieldId: string) => void;
  onFieldUpdate?: (fieldId: string, updates: Partial<FormField>) => void;
}

const FieldCard: React.FC<FieldCardProps> = ({
  field,
  index,
  totalFields,
  onDelete,
  onMoveUp,
  onMoveDown,
  onFieldUpdate
}) => {
  const { t } = useTranslation('forms');
  const [showToken, setShowToken] = useState(false);
  
  // Estados para nombre y descripción del campo
  const [fieldName, setFieldName] = useState(() => {
    // Si ya hay un nombre en el campo (modo edición), usarlo siempre
    if (field.name) {
      return field.name;
    }
    
    // Para campos nuevos estándar, usar nombres predeterminados para casos especiales
    if (field.type === 'standard') {
      if (field.inputType === 'email') {
        return 'Correo Electrónico';
      }
      if (field.inputType === 'date') {
        return 'Fecha';
      }
      return '';
    }
    
    // Para atributos e integraciones nuevos, usar el nombre que viene del maestro
    return field.name || '';
  });
  const [fieldDescription, setFieldDescription] = useState(() => {
    // Si ya hay una descripción en el campo (modo edición), usarla siempre
    if (field.description) {
      return field.description;
    }
    
    // Para campos nuevos estándar, usar descripciones predeterminadas para casos especiales
    if (field.type === 'standard') {
      if (field.inputType === 'email') {
        return 'Este campo se utiliza para recopilar la dirección de correo electrónico del usuario, que puede ser usado para comunicaciones, autenticación o envío de información.';
      }
      if (field.inputType === 'date') {
        return 'Este campo permite seleccionar una fecha específica, útil para programar eventos, fechas de nacimiento, fechas límite, etc.';
      }
    }
    return field.description || '';
  });
  
  // Sincronizar estado local cuando el campo cambie (útil para modo edición)
  useEffect(() => {
    if (field.name && field.name !== fieldName) {
      setFieldName(field.name);
    }
    if (field.description && field.description !== fieldDescription) {
      setFieldDescription(field.description);
    }
  }, [field.name, field.description, fieldName, fieldDescription]);
  
  const [description, setDescription] = useState(() => {
    if (field.inputType === 'email') {
      return 'Este campo se utiliza para recopilar la dirección de correo electrónico del usuario, que puede ser usado para comunicaciones, autenticación o envío de información.';
    }
    if (field.inputType === 'date' || (field.type === 'attribute' && field.dataType === 'date')) {
      return 'Este campo permite seleccionar una fecha específica, útil para programar eventos, fechas de nacimiento, fechas límite, etc.';
    }
    return '';
  });

  // Estados para configuración de API
  const [apiConfig, setApiConfig] = useState({
    url: field.apiConfig?.url || '',
    method: field.apiConfig?.method || '',
    authType: field.apiConfig?.authType || '',
    authToken: field.apiConfig?.authToken || '',
    headers: field.apiConfig?.headers || '',
    body: field.apiConfig?.body || '',
    format: field.apiConfig?.format || 'json',
    executeOnSubmit: field.apiConfig?.executeOnSubmit ?? true,
    showResponse: field.apiConfig?.showResponse ?? false,
    continueOnError: field.apiConfig?.continueOnError ?? false
  });

  // Función para actualizar la configuración de API
  const updateApiConfig = (updates: Partial<typeof apiConfig>) => {
    const newConfig = { ...apiConfig, ...updates };
    setApiConfig(newConfig);
    
    if (onFieldUpdate) {
      onFieldUpdate(field.id, {
        apiConfig: newConfig
      });
    }
  };

  // Función para actualizar el nombre del campo
  const updateFieldName = (name: string) => {
    setFieldName(name);
    if (onFieldUpdate) {
      onFieldUpdate(field.id, {
        name: name,
        label: name
      });
    }
  };

  // Función para actualizar la descripción del campo
  const updateFieldDescription = (desc: string) => {
    setFieldDescription(desc);
    if (onFieldUpdate) {
      onFieldUpdate(field.id, {
        description: desc
      });
    }
  };

  const isFirst = index === 0;
  const isLast = index === totalFields - 1;
  const isApiField = field.inputType === 'api';

  const getBorderColor = () => {
    if (field.type === 'attribute') return 'border-blue-200';
    if (field.type === 'integration') return 'border-green-200';
    return 'border-gray-200';
  };

  const renderFieldInput = () => {
    switch (field.inputType) {
      case 'text':
      case 'email':
      case 'number':
        return renderBasicInput();
      case 'checkbox':
        return renderCheckboxInput();
      case 'date':
        return renderDateInput();
      case 'select':
      case 'closelist':
      case 'openlist':
        return renderSelectInput();
      case 'file':
        return renderFileInput();
      case 'api':
        return renderApiInput();
      default:
        // Para campos de atributos que usan dataType
        if (field.type === 'attribute') {
          return renderAttributeInput();
        }
        return renderBasicInput();
    }
  };

  const renderBasicInput = () => {
    const fieldTypeLabel = field.inputType === 'email' ? 'Email' : 
                           field.inputType === 'number' ? 'Número' : 'Texto';

    return (
      <div className="space-y-3">
        {/* Campo de nombre */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Nombre del campo *</label>
          <input
            type="text"
            placeholder="Nombre del campo"
            value={fieldName}
            onChange={(e) => updateFieldName(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
          <textarea
            placeholder="Describe para qué se usa este campo"
            value={fieldDescription}
            onChange={(e) => updateFieldDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>

        {/* Tipo de dato */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de dato</label>
          <div className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-md">
            {fieldTypeLabel}
          </div>
        </div>

        {renderFieldOptions()}
      </div>
    );
  };

  const renderAttributeInput = () => {
    switch (field.dataType) {
      case 'text':
        return (
          <div className="space-y-3">
            {/* Campo de nombre */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nombre del campo *</label>
              <input
                type="text"
                placeholder="Nombre del campo"
                value={fieldName}
                onChange={(e) => updateFieldName(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
              <textarea
                placeholder="Describe para qué se usa este campo"
                value={fieldDescription}
                onChange={(e) => updateFieldDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            {/* Tipo de dato */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de dato</label>
              <div className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-md">
                Texto
              </div>
            </div>

            {renderFieldOptions()}
          </div>
        );
      case 'number':
        return (
          <div className="space-y-3">
            {/* Campo de nombre */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nombre del campo *</label>
              <input
                type="text"
                placeholder="Nombre del campo"
                value={fieldName}
                onChange={(e) => updateFieldName(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
              <textarea
                placeholder="Describe para qué se usa este campo"
                value={fieldDescription}
                onChange={(e) => updateFieldDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            {/* Tipo de dato */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de dato</label>
              <div className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-md">
                Número
              </div>
            </div>

            {renderFieldOptions()}
          </div>
        );
      case 'boolean':
        return renderCheckboxInput();
      case 'date':
        return renderDateInput();
      case 'closelist':
      case 'openlist':
        return renderSelectInput();
      default:
        return renderBasicInput();
    }
  };

  const renderCheckboxInput = () => (
    <div className="space-y-3">
      {/* Campo de nombre */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Nombre del campo *</label>
        <input
          type="text"
          placeholder="Nombre del campo"
          value={fieldName}
          onChange={(e) => updateFieldName(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Descripción */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
        <textarea
          placeholder="Describe para qué se usa este campo"
          value={fieldDescription}
          onChange={(e) => updateFieldDescription(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
      </div>

      {/* Tipo de dato */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de dato</label>
        <div className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-md">
          Sí/No (Boolean)
        </div>
      </div>

      {renderFieldOptions()}
    </div>
  );

  const renderDateInput = () => (
    <div className="space-y-3">
      {/* Campo de nombre */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Nombre del campo *</label>
        <input
          type="text"
          placeholder="Nombre del campo"
          value={fieldName}
          onChange={(e) => updateFieldName(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Descripción */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
        <textarea
          placeholder="Describe para qué se usa este campo"
          value={fieldDescription}
          onChange={(e) => updateFieldDescription(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
      </div>

      {/* Tipo de dato */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de dato</label>
        <div className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-md">
          Fecha
        </div>
      </div>

      {renderFieldOptions()}
    </div>
  );

  const renderSelectInput = () => {
    const [newOption, setNewOption] = React.useState('');
    // Inicializar options con las opciones del atributo si existen, sino con field.options
    const [options, setOptions] = React.useState<string[]>(() => {
      if (field.type === 'attribute' && field.closelist) {
        return field.closelist;
      }
      return field.options || [];
    });
    const [isMultiple, setIsMultiple] = React.useState(field.multiple || false);

    const addOption = () => {
      if (newOption.trim() && !options.includes(newOption.trim())) {
        const newOptions = [...options, newOption.trim()];
        setOptions(newOptions);
        setNewOption('');
        
        // Actualizar el campo principal
        if (onFieldUpdate) {
          onFieldUpdate(field.id, {
            options: newOptions,
            closelist: newOptions // Para atributos, también actualizar closelist
          });
        }
      }
    };

    const removeOption = (index: number) => {
      const newOptions = options.filter((_, i) => i !== index);
      setOptions(newOptions);
      
      // Actualizar el campo principal
      if (onFieldUpdate) {
        onFieldUpdate(field.id, {
          options: newOptions,
          closelist: newOptions // Para atributos, también actualizar closelist
        });
      }
    };

    const updateMultiple = (multiple: boolean) => {
      setIsMultiple(multiple);
      if (onFieldUpdate) {
        onFieldUpdate(field.id, {
          multiple: multiple
        });
      }
    };

    return (
      <div className="space-y-3">
        {/* Campo de nombre */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Nombre del campo *</label>
          <input
            type="text"
            placeholder="Nombre del campo"
            value={fieldName}
            onChange={(e) => updateFieldName(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
          <textarea
            placeholder="Describe para qué se usa este campo"
            value={fieldDescription}
            onChange={(e) => updateFieldDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>

        {/* Tipo de dato */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de dato</label>
          <div className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-md">
            {(field.dataType === 'openlist' || field.inputType === 'openlist') ? 'Lista Abierta' : 
             (field.dataType === 'closelist' || field.inputType === 'closelist' || field.inputType === 'select') ? 'Lista Cerrada' : 'Lista'}
          </div>
        </div>
        {/* Opciones solo para lista cerrada */}
        {((field.inputType === 'select') || (field.dataType === 'closelist') || (field.inputType === 'closelist')) && 
         (field.dataType !== 'openlist' && field.inputType !== 'openlist') && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Opciones de la lista</label>
            
            <div className="space-y-3">
              {/* Solo mostrar la opción de agregar si NO es un atributo */}
              {field.type !== 'attribute' && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Agregar nueva opción"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addOption();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={addOption}
                  >
                    Agregar
                  </button>
                </div>
              )}

              {/* Mostrar información sobre atributos predefinidos */}
              {field.type === 'attribute' && (
                <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded p-2">
                  📋 Las opciones vienen predefinidas desde las características del usuario y no se pueden modificar aquí.
                </div>
              )}

              {options.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-medium text-gray-600">Opciones actuales:</div>
                  <div className="flex flex-wrap gap-2">
                    {options.map((option, index) => (
                      <div key={index} className="flex items-center gap-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-sm">
                        <span>{option}</span>
                        {/* Solo mostrar botón de eliminar si NO es un atributo */}
                        {field.type !== 'attribute' && (
                          <button
                            type="button"
                            className="text-red-400 hover:text-red-600 text-xs"
                            onClick={() => removeOption(index)}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <label className="flex items-center space-x-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    checked={isMultiple}
                    onChange={(e) => updateMultiple(e.target.checked)}
                    disabled={field.type === 'attribute'} // Deshabilitar para atributos
                  />
                  <span className={field.type === 'attribute' ? 'text-gray-400' : ''}>
                    Permitir selección múltiple
                    {field.type === 'attribute' && ' (predefinido)'}
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        {renderFieldOptions()}
      </div>
    );
  };

  const renderFileInput = () => (
    <div className="space-y-3">
      {/* Campo de nombre */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Nombre del campo *</label>
        <input
          type="text"
          placeholder="Nombre del campo"
          value={fieldName}
          onChange={(e) => updateFieldName(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Descripción */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
        <textarea
          placeholder="Describe qué tipo de archivo se necesita"
          value={fieldDescription}
          onChange={(e) => updateFieldDescription(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
      </div>

      {/* Tipo de dato */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de dato</label>
        <div className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-md">
          Archivo
        </div>
      </div>

      <div className="space-y-1">
        <label className="flex items-center space-x-2 text-sm text-gray-600">
          <input
            type="checkbox"
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            defaultChecked={true}
          />
          <span>Es un campo obligatorio</span>
        </label>
      </div>
    </div>
  );

  const renderApiInput = () => (
    <div>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span className="text-sm font-medium text-gray-700">Configuración de API</span>
        </div>
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">URL del Endpoint *</label>
            <input
              type="url"
              placeholder="https://api.ejemplo.com/endpoint"
              value={apiConfig.url}
              onChange={(e) => updateApiConfig({ url: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Método HTTP *</label>
            <select 
              value={apiConfig.method}
              onChange={(e) => updateApiConfig({ method: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Selecciona un método</option>
              <option value="GET">GET - Obtener datos</option>
              <option value="POST">POST - Crear nuevos datos</option>
              <option value="PUT">PUT - Actualizar datos completos</option>
              <option value="PATCH">PATCH - Actualizar datos parciales</option>
              <option value="DELETE">DELETE - Eliminar datos</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de Autorización</label>
            <div className="space-y-2">
              <select 
                value={apiConfig.authType}
                onChange={(e) => updateApiConfig({ authType: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Sin autorización</option>
                <option value="bearer">Bearer Token (JWT, OAuth)</option>
                <option value="apikey-header">API Key en Header</option>
                <option value="apikey-query">API Key en Query Parameter</option>
                <option value="basic">Basic Authentication</option>
                <option value="oauth2">OAuth 2.0</option>
                <option value="custom">Autorización Personalizada</option>
              </select>
              <div className="relative">
                <input
                  type={showToken ? "text" : "password"}
                  placeholder="Token, API Key o credenciales de autorización"
                  value={apiConfig.authToken}
                  onChange={(e) => updateApiConfig({ authToken: e.target.value })}
                  className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                  title={showToken ? "Ocultar token" : "Mostrar token"}
                >
                  {showToken ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Formato de Datos</label>
            <select 
              value={apiConfig.format}
              onChange={(e) => updateApiConfig({ format: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="json">JSON (application/json)</option>
              <option value="form">Form Data (application/x-www-form-urlencoded)</option>
              <option value="multipart">Multipart Form (multipart/form-data)</option>
              <option value="xml">XML (application/xml)</option>
              <option value="text">Texto plano (text/plain)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Headers Adicionales (Opcional)</label>
            <textarea
              placeholder={'{"X-Custom-Header": "valor", "User-Agent": "Mi-App/1.0"}'}
              value={apiConfig.headers}
              onChange={(e) => updateApiConfig({ headers: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
            <div className="text-xs text-gray-500 mt-1">
              Formato JSON válido. Los headers estándar se agregan automáticamente.
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Body/Payload (Para POST/PUT/PATCH)</label>
            <textarea
              placeholder={`{"campo1": "{{form.campo1}}", "campo2": "{{form.campo2}}"}`}
              value={apiConfig.body}
              onChange={(e) => updateApiConfig({ body: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
            <div className="text-xs text-gray-500 mt-1">
              Usa {`{{form.nombreCampo}}`} para incluir datos del formulario
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-3 space-y-2">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <h4 className="text-xs font-medium text-blue-800 mb-2">Comportamiento de Ejecución</h4>
          <div className="space-y-1">
            <label className="flex items-center space-x-2 text-xs text-gray-700">
              <input
                type="checkbox"
                className="w-3 h-3 text-green-600 border-gray-300 rounded focus:ring-green-500"
                checked={apiConfig.executeOnSubmit}
                onChange={(e) => updateApiConfig({ executeOnSubmit: e.target.checked })}
              />
              <span>Ejecutar automáticamente cuando se envíe el formulario</span>
            </label>
            <label className="flex items-center space-x-2 text-xs text-gray-700">
              <input
                type="checkbox"
                className="w-3 h-3 text-green-600 border-gray-300 rounded focus:ring-green-500"
                checked={apiConfig.showResponse}
                onChange={(e) => updateApiConfig({ showResponse: e.target.checked })}
              />
              <span>Mostrar respuesta de la API al usuario</span>
            </label>
            <label className="flex items-center space-x-2 text-xs text-gray-700">
              <input
                type="checkbox"
                className="w-3 h-3 text-green-600 border-gray-300 rounded focus:ring-green-500"
                checked={apiConfig.continueOnError}
                onChange={(e) => updateApiConfig({ continueOnError: e.target.checked })}
              />
              <span>Continuar con el formulario si la API falla</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFieldOptions = () => (
    <div className="space-y-1">
      <label className="flex items-center space-x-2 text-sm text-gray-600">
        <input
          type="checkbox"
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          defaultChecked={field.required ?? true}
          onChange={(e) => {
            if (onFieldUpdate) {
              onFieldUpdate(field.id, {
                required: e.target.checked
              });
            }
          }}
        />
        <span>{t('labels.required_field', 'Campo requerido')}</span>
      </label>
      
      <label className="flex items-center space-x-2 text-sm text-gray-600">
        <input
          type="checkbox"
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          defaultChecked={field.requestFromUser ?? true}
          onChange={(e) => {
            if (onFieldUpdate) {
              onFieldUpdate(field.id, {
                requestFromUser: e.target.checked
              });
            }
          }}
        />
        <span>{t('labels.request_from_user')}</span>
      </label>
      
      {field.type === 'attribute' && (
        <>
          <label className="flex items-center space-x-2 text-sm text-gray-600">
            <input
              type="checkbox"
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              defaultChecked={field.updatable || false}
              onChange={(e) => {
                if (onFieldUpdate) {
                  onFieldUpdate(field.id, {
                    updatable: e.target.checked
                  });
                }
              }}
            />
            <span>Este campo es actualizable</span>
          </label>
          <div className="text-xs text-gray-500 mt-1 space-y-1">
            <div>* Las características fueron extraídas por IA de conversaciones anteriores; puedes editar este campo si lo consideras necesario.</div>
            <div>* Si el sistema no dispone de este dato, se le solicitará durante la interacción.</div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div
      className={`bg-white border-2 rounded-lg p-4 mb-3 ${getBorderColor()} relative`}
    >
      {/* Delete button and Move buttons */}
      <div className="absolute top-2 right-2 flex items-center space-x-1">
        {/* Move buttons - hidden for API fields */}
        {!isApiField && (
          <>
            {/* Move Up button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoveUp(field.id);
              }}
              disabled={isFirst}
              className={`p-1 rounded transition-colors ${
                isFirst
                  ? 'text-gray-300 cursor-not-allowed' 
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
              title="Mover arriba"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                <path d="M6 3l3 3H7v3H5V6H3l3-3z"/>
              </svg>
            </button>
            
            {/* Move Down button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoveDown(field.id);
              }}
              disabled={isLast}
              className={`p-1 rounded transition-colors ${
                isLast
                  ? 'text-gray-300 cursor-not-allowed' 
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
              title="Mover abajo"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                <path d="M6 9L3 6h2V3h2v3h2L6 9z"/>
              </svg>
            </button>
          </>
        )}

        {/* Delete button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(field.id);
          }}
          className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded p-1 transition-colors"
          title="Eliminar campo"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path fillRule="evenodd" d="M4 1a1 1 0 011-1h2a1 1 0 011 1v1h3a.5.5 0 010 1H10v7a2 2 0 01-2 2H4a2 2 0 01-2-2V3H1a.5.5 0 010-1h3V1zm1 0v1h2V1H5zM3 3v7a1 1 0 001 1h4a1 1 0 001-1V3H3zm1 1a.5.5 0 01.5.5v4a.5.5 0 01-1 0v-4A.5.5 0 014 4zm3 0a.5.5 0 01.5.5v4a.5.5 0 01-1 0v-4A.5.5 0 017 4z" clipRule="evenodd"/>
          </svg>
        </button>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">{field.label || field.name}</label>
          {field.type === 'attribute' && (
            <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full">
              Caracteristica
            </span>
          )}
          {field.type === 'integration' && (
            <span className="px-2 py-1 bg-green-100 text-green-600 text-xs rounded-full">
              Integración
            </span>
          )}
        </div>
        {field.type === 'attribute' && (
          <div className="flex items-center text-blue-600">
            {/* Icono o indicador adicional para atributos */}
          </div>
        )}
      </div>
      
      {renderFieldInput()}
    </div>
  );
};

export default FieldCard;
